import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Campaign } from '../../types/commerce';
import './Carousel.css';

export const ROTATE_MS = 5000;
interface Props {
  slides: readonly Campaign[];
  label: string;
  leading?: ReactNode;
  /** Shown when it arrives (e.g. a running sale), until the viewer takes over. */
  featuredId?: string;
  compact?: boolean;
}
export default function Carousel({ slides, label, leading, featuredId, compact = false }: Props) {
  const ids = useMemo(
    () => [...(leading ? ['hero'] : []), ...slides.map((slide) => slide.id)],
    [leading, slides],
  );
  const [selection, setSelection] = useState({ id: ids[0] ?? '', index: 0 });
  const found = ids.indexOf(selection.id);
  const index = found >= 0 ? found : Math.max(0, Math.min(selection.index, ids.length - 1));
  const activeId = ids[index];
  const order = ids.join('|');
  const root = useRef<HTMLDivElement>(null);
  const track = useRef<HTMLDivElement>(null);
  const activeIndex = useRef(index);
  activeIndex.current = index;
  const programmatic = useRef(false);
  const scrollingTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [paused, setPaused] = useState(false);
  const pausedNow = useRef(paused);
  pausedNow.current = paused;
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [visible, setVisible] = useState(false);
  const [hidden, setHidden] = useState(document.hidden);
  const [reduced, setReduced] = useState(
    () => !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
  );
  const count = ids.length;
  const hasSlides = count > 0;

  const scrollTo = useCallback(
    (next: number, smooth = true) => {
      const viewport = track.current;
      if (!viewport) return;
      programmatic.current = true;
      clearTimeout(scrollingTimer.current);
      viewport.scrollTo?.({
        left: next * viewport.clientWidth,
        behavior: smooth && !reduced ? 'smooth' : 'auto',
      });
      scrollingTimer.current = setTimeout(() => {
        programmatic.current = false;
      }, 700);
    },
    [reduced],
  );
  const move = useCallback(
    (next: number, manual = true) => {
      if (!count) return;
      const target = (next + count) % count;
      if (manual) setPaused(true);
      setSelection({ id: ids[target], index: target });
      scrollTo(target);
    },
    [count, ids, scrollTo],
  );

  useEffect(() => {
    setSelection((previous) =>
      previous.id === activeId && previous.index === index ? previous : { id: activeId ?? '', index },
    );
  }, [activeId, index]);
  useEffect(() => {
    scrollTo(activeIndex.current, false);
    const resize = () => scrollTo(activeIndex.current, false);
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [order, scrollTo]);
  useEffect(() => {
    const target = featuredId ? order.split('|').indexOf(featuredId) : -1;
    if (target < 0 || pausedNow.current) return;
    setSelection({ id: featuredId ?? '', index: target });
    scrollTo(target, false);
  }, [featuredId, order, scrollTo]);
  useEffect(() => {
    const motion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const changed = () => setReduced(!!motion?.matches);
    const visibility = () => setHidden(document.hidden);
    motion?.addEventListener?.('change', changed);
    document.addEventListener('visibilitychange', visibility);
    const observer =
      'IntersectionObserver' in window
        ? new IntersectionObserver((entries) => setVisible(entries.some((entry) => entry.isIntersecting)))
        : null;
    if (root.current) observer?.observe(root.current);
    if (!observer) setVisible(true);
    return () => {
      observer?.disconnect();
      motion?.removeEventListener?.('change', changed);
      document.removeEventListener('visibilitychange', visibility);
      clearTimeout(scrollingTimer.current);
    };
  }, [hasSlides]);

  const rotating = count > 1 && !paused && !hovered && !focused && !hidden && !reduced && visible;
  useEffect(() => {
    if (!rotating) return;
    const timer = setTimeout(() => move(index + 1, false), ROTATE_MS);
    return () => clearTimeout(timer);
  }, [rotating, index, move]);

  if (!count) return null;
  return (
    <div
      ref={root}
      className={`carousel${compact ? ' carousel--compact' : ''}`}
      role={compact ? 'group' : 'region'}
      aria-roledescription="carousel"
      aria-label={label}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocused(false);
      }}
    >
      <div
        ref={track}
        role="group"
        aria-label={`${label} slides`}
        className="carousel__track"
        aria-live={paused || reduced ? 'polite' : 'off'}
        onPointerDown={() => {
          programmatic.current = false;
          setPaused(true);
        }}
        onWheel={() => {
          programmatic.current = false;
          setPaused(true);
        }}
        onScroll={() => {
          const viewport = track.current;
          if (programmatic.current || !viewport?.clientWidth) return;
          const next = Math.max(
            0,
            Math.min(count - 1, Math.round(viewport.scrollLeft / viewport.clientWidth)),
          );
          setSelection({ id: ids[next], index: next });
          setPaused(true);
        }}
      >
        {leading && (
          <div
            className="carousel__slide"
            role="group"
            aria-roledescription="slide"
            aria-label={`1 of ${count}`}
            aria-hidden={index !== 0}
            inert={index !== 0}
          >
            {leading}
          </div>
        )}
        {slides.map((slide, position) => {
          const number = position + (leading ? 1 : 0);
          const accent = slide.color === 'gold' || slide.color === 'blue' ? slide.color : 'burgundy';
          return (
            <div
              key={slide.id}
              className={`carousel__slide carousel__slide--${accent}`}
              role="group"
              aria-roledescription="slide"
              aria-label={`${number + 1} of ${count}`}
              aria-hidden={index !== number}
              inert={index !== number}
            >
              <Link className="carousel__campaign" to={slide.destination}>
                <img src={slide.image_url} alt="" loading={number === index ? 'eager' : 'lazy'} />
                <div className="carousel__copy">
                  <h2>{slide.title}</h2>
                  {slide.subtitle && <p>{slide.subtitle}</p>}
                  <span className="carousel__cta">{slide.cta_label}</span>
                </div>
              </Link>
            </div>
          );
        })}
      </div>
      {count > 1 && (
        <div className="carousel__controls">
          <button type="button" aria-label={`Previous ${label} slide`} onClick={() => move(index - 1)}>
            ‹
          </button>
          <div role="group" aria-label={`Choose ${label} slide`} className="carousel__dots">
            {ids.map((id, number) => (
              <button
                key={id}
                type="button"
                aria-label={`Show ${label} slide ${number + 1}`}
                aria-disabled={number === index}
                onClick={() => move(number)}
              >
                <span aria-hidden="true">●</span>
              </button>
            ))}
          </div>
          <button type="button" aria-label={`Next ${label} slide`} onClick={() => move(index + 1)}>
            ›
          </button>
        </div>
      )}
    </div>
  );
}
